import type { IkeEnvironment } from '@ikegps/core-api';
import type { IkeConfigKey } from '@ikegps/core-shared';
import { type ModalResponse, ModalService } from '../components/modal/modal.service';
import { T, Translate } from '../i18n';
import { type IkeClientService, Services } from './index';

export interface BoundWindow extends Window {
	encodeURIComponent: (str: string) => string;
	decodeURIComponent: (encodedURI: string) => string;
	ResizeObserver: typeof ResizeObserver;
	ga: UniversalAnalytics.ga;
	/** @deprecated Only use for Parse backwards-compatibility */
	Parse: any;
	IKE_APPLICATION_VERSION: string;
	IKE_ENVIRONMENT: IkeEnvironment;
	IKE_CONFIG: Partial<{
		[key in IkeConfigKey]: string;
	}>;
}

export interface PageNavigationListener {
	msg: T;
	stopNavigate: () => boolean;
	/** Optional custom title for the internal navigation modal. When omitted, defaults to "Discard changes". */
	modalTitle?: T;
	/** Optional custom content for the internal navigation modal. When omitted, defaults to "Are you sure you want to discard unsaved changes?". */
	modalContent?: T;
	/** When true, the modal only shows an OK button (no cancel) — navigation is blocked entirely. */
	blockNavigation?: boolean;
}

/**
 * Manages the native `window` object.
 *
 * Responsibilities include:
 * - mitigating browser incompatibilities
 * - propagating changes with legacy Angular codebase
 */
export class WindowService implements IkeClientService {
	__priority = 0;

	$window?: BoundWindow;

	/** Whether navigation is being processed. Used to cancel duplicate navigation events from multiple bindings. */
	isNavigating: boolean = false;

	/** Whether navigation events have been bound to `window`. Used to ensure navigation events are bound once. */
	private isStateBound: boolean = false;

	/** Navigation callbacks. */
	private navigationListeners: PageNavigationListener[] = [];

	setWindow(window: BoundWindow) {
		this.$window = window;
	}

	get window(): BoundWindow {
		if (this.$window == null) {
			throw new Error('WindowService has not started');
		}
		return this.$window;
	}

	get document(): Document {
		if (this.$window == null) {
			throw new Error('WindowService has not started');
		}
		return this.$window.document;
	}

	/**
	 * Open a URL in a new browsing context. Callers depend on this service (the app's
	 * window abstraction) rather than the global, which also gives tests a stub surface.
	 */
	open(url: string, target: string = '_blank'): void {
		this.window.open(url, target);
	}

	/**
	 * Register a callback to be invoked when navigating to another page.
	 *
	 * @param msg Confirmation message when confirming navigation.
	 * @param stopNavigate Callback which returns true or false to confirm or halt navigation.
	 * @param options Optional modal customization for internal navigation.
	 */
	bindNavigateAway(
		msg: T,
		stopNavigate: () => boolean,
		options?: Pick<PageNavigationListener, 'modalTitle' | 'modalContent' | 'blockNavigation'>
	) {
		this.ensureWindowEvents();
		this.navigationListeners.push({ msg, stopNavigate, ...options });
	}

	/** De-register navigation callback function */
	unbindNavigateAway(stopNavigate: () => boolean) {
		this.navigationListeners = this.navigationListeners.filter((listener) => listener.stopNavigate !== stopNavigate);
	}

	/** De-register all navigation callback function */
	unbindAllNavigateAway() {
		this.navigationListeners.forEach((l) => this.unbindNavigateAway(l.stopNavigate));
	}

	private ensureWindowEvents() {
		if (this.isStateBound) {
			return;
		}

		this.$window?.addEventListener('beforeunload', this.confirmPageUnload.bind(this));

		this.isStateBound = true;
	}

	private confirmPageUnload(event: BeforeUnloadEvent): string | undefined {
		const listener = this.navigationListeners.find((l) => l.stopNavigate());
		if (listener == null) {
			delete event.returnValue;
			return undefined;
		}

		event.preventDefault();
		const message = Translate(listener.msg);
		event.returnValue = message;
		return message;
	}

	async confirmPageChange(event: Event, url: string): Promise<boolean> {
		event.preventDefault();
		if (this.isNavigating) {
			return false;
		}

		this.isNavigating = true;

		const listener = this.navigationListeners.find((l) => l.stopNavigate());
		let result: ModalResponse = {
			ok: true,
			cancel: false,
		};

		if (listener) {
			ModalService.close();

			if (listener.blockNavigation) {
				await ModalService.open({
					title: listener.modalTitle ?? T.DISCARD_CHANGES_TITLE,
					content: Translate(listener.modalContent ?? listener.msg),
					hasCancel: false,
				});
				this.isNavigating = false;
				return false;
			}

			result = await ModalService.open({
				title: listener.modalTitle ?? T.DISCARD_CHANGES_TITLE,
				content: Translate(listener.modalContent ?? T.DISCARD_CHANGES_CONFIRM),
				okName: T.DISCARD_CHANGES_DISCARD,
				hasCancel: true,
			});
		}

		if (result.ok) {
			this.unbindAllNavigateAway();
			this.isNavigating = false;
			Services.Window.$window?.open(url, '_top');
			return true;
		}

		this.isNavigating = false;
		return false;
	}
}
